// Quarantine an un-syncable capture_items count instead of dropping it.
//
// When a capture_items create or increment can NEVER land on the server — its
// FK parent (space/session) is absent so the backend returns 404, and the
// count could not be re-homed onto an authoritative row — the connector must
// still ACK the CRUD entry to drain the upload queue (throwing would wedge the
// WHOLE queue; see connector.ts). Historically that ack DELETEd the local row
// and emitted `dropped`: the surveyor's count, which lived ONLY on this device,
// was lost with nothing but a Sentry line behind it.
//
// Per "never silently drop a count" (AGENTS.md "Business criticality"), we
// instead COPY the payload into the device-local `orphaned_capture_items` table
// (localOnly — never synced, survives restarts) BEFORE removing the un-syncable
// row, then ack. The count is preserved on the device and a recovery surface
// lets the surveyor see and re-inject it. Telemetry still fires so the sync
// failure stays visible.
//
// Context fidelity: a create CRUD entry's `opData` carries the FULL row
// (capture_session_id / space_id / asset_type_id / …), so a quarantined create
// is fully recoverable. A PATCH entry's `opData` carries only the changed
// columns (quantity / is_zero_confirmed / notes) and its local row is already
// gone by the time it 404s, so a quarantined patch increment preserves the
// count value but may lack FK context — still strictly better than a silent drop.

import type { CrudEntry } from '@powersync/web'
import type { PowerSyncWriteDb } from './powersync-db'
import * as Sentry from '@sentry/react'

import { invalidFieldsFrom } from './connector-error-details'
import { getOptionalRow, powerSyncRowExists } from './powersync-local-db-utils'
import { _emitQuarantined } from './upload-events'

// The re-quarantine merge UPDATE. Extracted as a constant so the real-engine
// contract test (orphaned-items-merge-sql.test.ts) runs the EXACT SQL the
// connector executes — against a real node:sqlite engine — instead of a copy
// that could drift from production.
//
// EVERY mutable column is COALESCE(?, existing): a re-quarantine PATCH whose
// opData omits a column (PowerSync's onlyWhenChanged drops unchanged columns —
// schema.ts) must PRESERVE the value a prior CREATE quarantine recorded, never
// erase it. This includes the count fields `quantity` and `is_zero_confirmed`:
// a confirm-zero on an already-zero item, or a notes-only edit, yields a PATCH
// with NO quantity, so a bare `quantity = ?` would null a preserved count
// (contractual data-loss — AGENTS.md "Business criticality"). A present value
// (an increment carries the absolute latest quantity) overwrites as before;
// `quantity` is never legitimately null (NOT NULL count), so COALESCE only ever
// guards the omitted-column case. `reason`/`orphaned_at` always refresh.
export const ORPHANED_ITEMS_MERGE_UPDATE_SQL = `UPDATE orphaned_capture_items SET
         quantity           = COALESCE(?, quantity),
         is_zero_confirmed  = COALESCE(?, is_zero_confirmed),
         notes              = COALESCE(?, notes),
         capture_session_id = COALESCE(?, capture_session_id),
         site_id            = COALESCE(?, site_id),
         building_id        = COALESCE(?, building_id),
         space_id           = COALESCE(?, space_id),
         asset_type_id      = COALESCE(?, asset_type_id),
         captured_at        = COALESCE(?, captured_at),
         reason             = ?,
         orphaned_at        = strftime('%Y-%m-%dT%H:%M:%fZ','now')
       WHERE id = ?`

/** Coerce a CRUD opData value to a string column value, or null if absent. */
function str(value: unknown): string | null {
  return value === null || value === undefined ? null : String(value)
}

/** FK/context + count columns a thin PATCH's opData lacks but the still-present
 * local row carries. */
interface LocalCaptureItemContext {
  readonly capture_session_id?: unknown
  readonly site_id?: unknown
  readonly building_id?: unknown
  readonly space_id?: unknown
  readonly asset_type_id?: unknown
  readonly captured_at?: unknown
  readonly quantity?: unknown
  readonly is_zero_confirmed?: unknown
  readonly notes?: unknown
}

function reportQuarantineTelemetry(
  entry: CrudEntry,
  err: Error,
  data: Record<string, unknown>,
  reason: string,
): void {
  try {
    // `invalidFields` carries the Zod field PATH(S) the backend rejected for an
    // `invalid_argument` (field NAMES only — see invalidFieldsFrom), `undefined`
    // (dropped by Sentry) for every other quarantine. This preserves #799's
    // "rejected on <field>" diagnostic on the DRAIN path now that #751 quarantines
    // a capture_items `invalid_argument` instead of letting it wedge+beacon —
    // closing the observability gap #751 would otherwise open. Safe here without
    // the no-PII allowlist: the quarantine telemetry already carries the full
    // `payload`, so it is deliberately NOT a no-PII sink (unlike beaconUploadJam).
    Sentry.captureException(err, {
      level: 'warning',
      extra: {
        reason,
        entryId: entry.id,
        payload: data,
        quarantined: true,
        invalidFields: invalidFieldsFrom(err),
      },
    })
  } catch {
    // Telemetry must never prevent quarantine persistence or queue draining.
  }
  try {
    console.warn(
      '[powersync] capture_items un-syncable — quarantined on device for recovery (NOT dropped)',
      { id: entry.id, reason, payload: data },
    )
  } catch {
    // Logging is best-effort for the same reason as Sentry.
  }
}

/**
 * Best-effort read of the still-present local `capture_items` row to recover the
 * FK context / `captured_at` AND the count fields (quantity / is_zero_confirmed /
 * notes) that a thin PATCH's opData omits (PowerSync's onlyWhenChanged drops
 * unchanged columns). Returns undefined when the row is already gone (a prior
 * quarantine for this id deleted it — the subsequent UPDATE COALESCE path
 * preserves that snapshot instead) or when the db has no `getOptional` (unit-test
 * mocks). Swallows read errors deliberately: this is ADDITIVE enrichment, so a
 * failed read just omits the optional context rather than wedging — the opposite
 * of the existence check below, which must propagate (audit-1 #1). (audit-1 #4;
 * data-accuracy ratchet)
 */
async function readLocalCaptureItem(
  db: PowerSyncWriteDb,
  id: string,
): Promise<LocalCaptureItemContext | undefined> {
  try {
    const row = await getOptionalRow(
      db,
      `SELECT capture_session_id, site_id, building_id, space_id,
              asset_type_id, captured_at, quantity, is_zero_confirmed, notes
         FROM capture_items WHERE id = ?`,
      [id],
    )
    return (row as LocalCaptureItemContext | null) ?? undefined
  } catch {
    return undefined
  }
}

/**
 * Recover the immutable FK context from the CRUD entry's `metadata` channel.
 *
 * The increment adapter (capture-client-powersync.ts flushCoalesce) writes the
 * row's own FK columns into `_metadata` via `json_object(...)` while the local
 * row is still present (schema.ts trackMetadata). Unlike opData — which value-
 * diffs the unchanged FK columns out of a thin increment PATCH — `metadata`
 * rides durably in the upload queue and survives the local row's sync-deletion.
 * So this is the ONLY FK source that remains when both opData (thin) and the
 * live `capture_items` row (already gone on a re-seed) lack it — exactly the
 * "bare recovery row" failure mode. Best-effort: any parse failure or non-object
 * payload yields undefined so the enrichment is simply skipped — it must NEVER
 * throw (that would re-wedge the upload queue this path exists to drain).
 */
function parseItemContextMetadata(
  metadata: string | undefined,
): LocalCaptureItemContext | undefined {
  if (metadata === undefined || metadata === '') return undefined
  try {
    const parsed: unknown = JSON.parse(metadata)
    if (typeof parsed !== 'object' || parsed === null) return undefined
    return parsed as LocalCaptureItemContext
  } catch {
    return undefined
  }
}

/**
 * Preserve-then-remove for a capture_items count that can never upload.
 *
 * Replaces the historical drop-and-ack. Copies it into the local-only
 * `orphaned_capture_items` quarantine, emits best-effort telemetry, removes the
 * un-syncable row from the synced table so the queue drains, and emits
 * `quarantined` so the UI can tell the surveyor their counts are HELD (not
 * lost) and offer recovery. `reason` distinguishes the cause in telemetry.
 * Never throws on the happy path; DB failures still throw, but telemetry
 * failures must not re-wedge the queue.
 */
export async function quarantineUnsyncableCount(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  // `Error`, not `ApiError`: the per-op carve-outs pass the backend `ApiError`,
  // but the central isolation backstop (connector-upload-isolation.ts) may pass
  // any throwable. Only used for the Sentry capture below, which accepts both.
  err: Error,
  data: Record<string, unknown>,
  reason: string,
): Promise<void> {
  // Recover from the still-present local capture_items row every column a thin
  // PATCH's opData omits (PowerSync's onlyWhenChanged drops unchanged columns —
  // schema.ts): FK/captured_at AND the count fields. The un-syncable row is
  // DELETEd later in this function, so for a FIRST quarantine via a thin PATCH it
  // is still present here and its count is recovered; for a RE-quarantine the
  // first quarantine already deleted it, so `local` is undefined and the
  // COALESCE-merge UPDATE preserves the prior orphan value instead. (audit-1 #4;
  // data-accuracy ratchet: a first thin-PATCH quarantine must not store a null
  // count while the local row still holds it.)
  const local = await readLocalCaptureItem(db, entry.id)
  // Final FK source, durable past the local row's sync-deletion (see
  // parseItemContextMetadata). Ordered LAST in each `??` chain: live opData and
  // the still-present local row both carry FRESHER values when available; the
  // metadata snapshot only fills the gap when both are absent (the thin-PATCH-
  // after-row-deletion case that produced the bare recovery rows). The FK
  // columns are immutable, so a stale-vs-fresh distinction does not arise for
  // them; `captured_at` is likewise create-time-fixed.
  const meta = parseItemContextMetadata(entry.metadata)

  // is_zero_confirmed: present flag overwrites; omitted → local row → null. A
  // present 0 is kept (`??` only falls through on null/undefined). null lets the
  // UPDATE COALESCE preserve the prior value; the INSERT path coerces null → 0.
  const zeroRaw = data['is_zero_confirmed'] ?? local?.is_zero_confirmed
  const isZeroConfirmed =
    zeroRaw === undefined || zeroRaw === null
      ? null
      : zeroRaw === 1 || zeroRaw === true
        ? 1
        : 0

  const captureSessionId =
    str(data['capture_session_id']) ??
    str(local?.capture_session_id) ??
    str(meta?.capture_session_id)
  const siteId =
    str(data['site_id']) ?? str(local?.site_id) ?? str(meta?.site_id)
  const buildingId =
    str(data['building_id']) ??
    str(local?.building_id) ??
    str(meta?.building_id)
  const spaceId =
    str(data['space_id']) ?? str(local?.space_id) ?? str(meta?.space_id)
  const assetTypeId =
    str(data['asset_type_id']) ??
    str(local?.asset_type_id) ??
    str(meta?.asset_type_id)
  const capturedAt =
    str(data['captured_at']) ??
    str(local?.captured_at) ??
    str(meta?.captured_at)

  // Non-destructive merge — NOT delete-then-insert, and NOT an UPSERT. PowerSync
  // exposes every table (local-only included) as a SQLite VIEW, and SQLite
  // rejects `INSERT … ON CONFLICT` on a view ("cannot UPSERT a view"); a plain
  // INSERT would instead PK-conflict on a re-quarantine and THROW, re-wedging the
  // very upload queue this path exists to drain. So we read-then-INSERT-or-UPDATE
  // by id (getOptional feature-detected; absent ⟹ no existing row ⟹ INSERT, the
  // shape execute-only stubs assert). A later thin PATCH quarantine for an id a
  // CREATE already quarantined must not clobber the create's snapshot: the UPDATE
  // (ORPHANED_ITEMS_MERGE_UPDATE_SQL) COALESCEs EVERY mutable column — including
  // the count fields quantity + is_zero_confirmed — so a column the patch omits
  // (PowerSync's onlyWhenChanged drops unchanged columns: a confirm-zero or
  // notes-only edit carries no quantity) PRESERVES the prior value instead of
  // nulling a preserved count. A present value (an increment carries the absolute
  // latest quantity) overwrites, latest-wins. (audit-1 #4; count-null fix)
  // A first thin-PATCH quarantine (INSERT path, no prior orphan row) falls back
  // to the still-present local row's count rather than storing null.
  const quantity = str(data['quantity']) ?? str(local?.quantity)
  const notes = str(data['notes']) ?? str(local?.notes)
  if (
    await powerSyncRowExists(
      db,
      `SELECT id FROM orphaned_capture_items WHERE id = ? LIMIT 1`,
      [entry.id],
    )
  ) {
    await db.execute(ORPHANED_ITEMS_MERGE_UPDATE_SQL, [
      quantity,
      isZeroConfirmed,
      notes,
      captureSessionId,
      siteId,
      buildingId,
      spaceId,
      assetTypeId,
      capturedAt,
      reason,
      entry.id,
    ])
  } else {
    await db.execute(
      `INSERT INTO orphaned_capture_items
         (id, original_item_id, capture_session_id, site_id, building_id,
          space_id, asset_type_id, quantity, is_zero_confirmed, notes,
          captured_at, reason, orphaned_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?,
               strftime('%Y-%m-%dT%H:%M:%fZ','now'))`,
      [
        entry.id,
        entry.id,
        captureSessionId,
        siteId,
        buildingId,
        spaceId,
        assetTypeId,
        quantity,
        // A CREATE always carries the flag; coerce a degenerate null → 0 so the
        // first INSERT never stores NULL (the UPDATE path keeps it nullable for
        // COALESCE-preserve).
        isZeroConfirmed ?? 0,
        notes,
        capturedAt,
        reason,
      ],
    )
  }

  reportQuarantineTelemetry(entry, err, data, reason)

  // Remove the un-syncable row so the upload queue drains. The resulting
  // capture_items DELETE acks as a local no-op in dispatchCrudEntry — the row
  // never existed server-side, so there is nothing to delete remotely.
  await db.execute(`DELETE FROM capture_items WHERE id = ?`, [entry.id])

  _emitQuarantined(entry.table, entry.id, reason)
}
